import { Checkbox, Select, Shimmer, TextTooltip } from '@components'
import {
  ACCOUNTING_SUGGESTIONS_DIGEST_FREQUENCIES,
  IUsershipNotificationPreferences,
  isAccountingSuggestionsDigestFrequency,
  NOTIFICATION_CATEGORY_KEYS,
  NOTIFICATION_CHANNEL_FIELD,
  NOTIFICATION_CHANNELS,
  NotificationCategory,
  NotificationChannel
} from '@query'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaQuestionCircle } from 'react-icons/fa'
import styled from 'styled-components'

interface Props {
  value: IUsershipNotificationPreferences
  onChange: (value: IUsershipNotificationPreferences) => void
}

const channelValues = (
  value: IUsershipNotificationPreferences,
  channel: NotificationChannel
): Record<NotificationCategory, boolean> => value[NOTIFICATION_CHANNEL_FIELD[channel]]

const filledChannel = (enabled: boolean): Record<NotificationCategory, boolean> =>
  Object.fromEntries(NOTIFICATION_CATEGORY_KEYS.map(key => [key, enabled])) as Record<
    NotificationCategory,
    boolean
  >

const COLUMN_SELECT_LABEL = {
  push: 'notificationFeed.preferences.allPush',
  email: 'notificationFeed.preferences.allEmail'
} as const

const preferenceState = (
  push: boolean,
  email: boolean
): 'both' | 'pushOnly' | 'emailOnly' | 'none' => {
  if (push && email) {
    return 'both'
  }
  if (push) {
    return 'pushOnly'
  }
  if (email) {
    return 'emailOnly'
  }
  return 'none'
}

export const NotificationPreferencesLoading: React.FC = () => {
  const { t } = useTranslation()

  return (
    <Table>
      <Header>
        <ColumnLabel $alignStart>
          {t('notificationFeed.preferences.columns.notification')}
        </ColumnLabel>
        <ColumnLabel>{t('notificationFeed.preferences.columns.push')}</ColumnLabel>
        <ColumnLabel>{t('notificationFeed.preferences.columns.email')}</ColumnLabel>
        <HeaderSpacer />
        <CheckCell $header>
          <Shimmer width="1.1rem" height="1.1rem" borderRadius="0.25rem" />
        </CheckCell>
        <CheckCell $header>
          <Shimmer width="1.1rem" height="1.1rem" borderRadius="0.25rem" />
        </CheckCell>
      </Header>
      {['40%', '48%', '52%', '36%', '30%', '55%'].map(width => (
        <SkeletonRow key={width}>
          <SkeletonCopy>
            <Shimmer width={width} height="0.75rem" />
            <Shimmer width="85%" height="0.65rem" />
          </SkeletonCopy>
          <CheckCell>
            <Shimmer width="1.1rem" height="1.1rem" borderRadius="0.25rem" />
          </CheckCell>
          <CheckCell>
            <Shimmer width="1.1rem" height="1.1rem" borderRadius="0.25rem" />
          </CheckCell>
        </SkeletonRow>
      ))}
    </Table>
  )
}

export const NotificationPreferencesForm: React.FC<Props> = ({ value, onChange }) => {
  const { t } = useTranslation()
  const digestFrequency = value.accounting_suggestions_digest_frequency
  const systemActive = value.categories.system || value.email_categories.system

  const setChannel = (channel: NotificationChannel, enabled: boolean) => {
    onChange({
      ...value,
      [NOTIFICATION_CHANNEL_FIELD[channel]]: filledChannel(enabled)
    })
  }

  const setCategory = (
    channel: NotificationChannel,
    key: NotificationCategory,
    enabled: boolean
  ) => {
    const field = NOTIFICATION_CHANNEL_FIELD[channel]
    onChange({
      ...value,
      [field]: { ...value[field], [key]: enabled }
    })
  }

  return (
    <Table>
      <Header>
        <ColumnLabel $alignStart>
          {t('notificationFeed.preferences.columns.notification')}
        </ColumnLabel>
        {NOTIFICATION_CHANNELS.map(channel => (
          <ColumnLabel key={channel} id={`notification-preferences-col-${channel}`}>
            {t(`notificationFeed.preferences.columns.${channel}`)}
          </ColumnLabel>
        ))}
        <HeaderSpacer />
        {NOTIFICATION_CHANNELS.map(channel => {
          const values = NOTIFICATION_CATEGORY_KEYS.map(key => channelValues(value, channel)[key])
          const allOn = values.every(Boolean)
          const mixed = values.some(Boolean) && !allOn
          return (
            <CheckCell key={channel} $header>
              <Checkbox
                ref={(input: HTMLInputElement | null) => {
                  if (input) {
                    input.indeterminate = mixed
                  }
                }}
                checked={allOn}
                aria-label={t(COLUMN_SELECT_LABEL[channel])}
                onChange={() => setChannel(channel, !allOn)}
              />
            </CheckCell>
          )
        })}
      </Header>
      {NOTIFICATION_CATEGORY_KEYS.map(key => {
        const title = t(`notificationFeed.preferences.categories.${key}.title`)
        const description = t(`notificationFeed.preferences.categories.${key}.description`)
        const push = value.categories[key]
        const email = value.email_categories[key]
        return (
          <Row key={key}>
            <CategoryRow>
              <Copy>
                <TitleLine>
                  <Label id={`notification-preferences-${key}`}>{title}</Label>
                  <TextTooltip placement="bottom-start" tooltip={description}>
                    <HelpButton type="button" aria-label={title}>
                      <FaQuestionCircle />
                    </HelpButton>
                  </TextTooltip>
                </TitleLine>
                <StateHint>
                  {t(`notificationFeed.preferences.state.${preferenceState(push, email)}`)}
                </StateHint>
              </Copy>
              <CheckCell>
                <Checkbox
                  checked={push}
                  aria-label={`${title}, ${t('notificationFeed.preferences.columns.push')}`}
                  onChange={event => setCategory('push', key, event.target.checked)}
                />
              </CheckCell>
              <CheckCell>
                <Checkbox
                  checked={email}
                  aria-label={`${title}, ${t('notificationFeed.preferences.columns.email')}`}
                  onChange={event => setCategory('email', key, event.target.checked)}
                />
              </CheckCell>
            </CategoryRow>
            {key === 'system' && (
              <DigestRow>
                <TitleLine>
                  <Label>{t('notificationFeed.preferences.suggestionsDigest.title')}</Label>
                  <TextTooltip
                    placement="bottom-start"
                    tooltip={t('notificationFeed.preferences.suggestionsDigest.description')}
                  >
                    <HelpButton
                      type="button"
                      aria-label={t('notificationFeed.preferences.suggestionsDigest.title')}
                    >
                      <FaQuestionCircle />
                    </HelpButton>
                  </TextTooltip>
                </TitleLine>
                <Select
                  name="notification-preferences-digest"
                  value={digestFrequency}
                  disabled={!systemActive}
                  hideErrorText
                  aria-label={t('notificationFeed.preferences.suggestionsDigest.title')}
                  style={{ marginBottom: 0, width: 'auto', minWidth: '8rem', flexShrink: 0 }}
                  onChange={event => {
                    const next = event.target.value
                    if (!isAccountingSuggestionsDigestFrequency(next)) {
                      return
                    }
                    onChange({
                      ...value,
                      accounting_suggestions_digest_frequency: next
                    })
                  }}
                >
                  {ACCOUNTING_SUGGESTIONS_DIGEST_FREQUENCIES.map(frequency => (
                    <option key={frequency} value={frequency}>
                      {t(`notificationFeed.preferences.suggestionsDigest.${frequency}`)}
                    </option>
                  ))}
                </Select>
              </DigestRow>
            )}
          </Row>
        )
      })}
    </Table>
  )
}

const Table = styled.div`
  display: flex;
  flex-direction: column;
`

const Header = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 3.75rem 3.75rem;
  grid-template-rows: auto auto;
  column-gap: ${({ theme }) => theme.spacing.xs}rem;
  row-gap: ${({ theme }) => theme.spacing.sm}rem;
  align-items: center;
  padding-bottom: ${({ theme }) => theme.spacing.md}rem;
  margin-bottom: ${({ theme }) => theme.spacing.xs}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutralGray};
`

const HeaderSpacer = styled.span`
  grid-column: 1;
  grid-row: 2;
`

const ColumnLabel = styled.span<{ $alignStart?: boolean }>`
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 700;
  text-align: ${({ $alignStart }) => ($alignStart ? 'left' : 'center')};
  justify-self: ${({ $alignStart }) => ($alignStart ? 'start' : 'center')};
`

const Row = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutralGray};

  &:last-child {
    border-bottom: none;
  }
`

const CategoryRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 3.75rem 3.75rem;
  column-gap: ${({ theme }) => theme.spacing.xs}rem;
  align-items: start;
  min-height: 2.75rem;
`

const Copy = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  padding: ${({ theme }) => theme.spacing.sm}rem 0;
`

const TitleLine = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
`

const Label = styled.span`
  color: ${({ theme }) => theme.colors.neutralBlack};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 600;
`

const StateHint = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.iconGray};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  line-height: 1.45;
`

const CheckCell = styled.div<{ $header?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding-top: ${({ theme, $header }) => ($header ? 0 : theme.spacing.sm)}rem;
  box-sizing: border-box;

  input[type='checkbox'] {
    accent-color: ${({ theme }) => theme.colors.primary};
  }

  & > div {
    margin-bottom: 0;
  }
`

const HelpButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.metalGray};
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
`

const DigestRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.md}rem;
  margin-top: ${({ theme }) => theme.spacing.md}rem;
`

const SkeletonRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 3.75rem 3.75rem;
  column-gap: ${({ theme }) => theme.spacing.xs}rem;
  align-items: start;
  height: 4rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutralGray};
`

const SkeletonCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`
